import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send } from 'lucide-react';
import { useLongPressShare } from '../ShareButton';

/**
 * Flyer Lightbox modal — extracted from App.js HomePage (Feb 2026 P3 refactor).
 * Shows a single flyer in an overlay with "Learn More" CTA.
 */
const FlyerLightboxModal = ({ open, flyer, onClose }) => {
  const imageSrc =
    flyer?.image_url?.startsWith('/api/')
      ? `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${flyer.image_url}`
      : flyer?.image_url;

  const longPress = useLongPressShare({
    kind: 'flyer',
    itemId: flyer?.id,
    title: flyer?.title || 'Vineyard Event',
    description: flyer?.description || '',
  });

  return (
    <AnimatePresence>
      {open && flyer && (
        <div
          data-testid="flyer-lightbox-modal"
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            className="relative max-w-4xl w-full max-h-[90vh] overflow-hidden"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              data-testid="flyer-lightbox-close"
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
              <img
                {...longPress}
                src={imageSrc}
                alt={flyer.title}
                className="w-full max-h-[70vh] object-contain bg-slate-100 select-none"
                draggable
              />
              <div className="p-6 bg-gradient-to-r from-amber-50 to-orange-50 border-t border-amber-200">
                <h3 className="text-2xl font-bold text-slate-800 mb-2">{flyer.title}</h3>
                {flyer.description && (
                  <p className="text-slate-600 mb-4">{flyer.description}</p>
                )}
                <div className="flex items-center gap-4">
                  {flyer.link_url && (
                    <a
                      href={flyer.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="flyer-lightbox-learn-more"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all font-medium"
                    >
                      <Send className="w-4 h-4" />
                      Learn More
                    </a>
                  )}
                  <button
                    onClick={onClose}
                    className="px-4 py-2 border-2 border-slate-300 text-slate-600 rounded-xl hover:bg-slate-100 transition-all font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default FlyerLightboxModal;
